// translations/pages/kyveil/ja.js

export const kyveilPageJA = {
  "seo": {
    "title": "Kyveil PII匿名化 | pAIoneer Labs GmbH",
    "description": "Kyveilは、個人を識別できる情報をお客様のシステム上でローカルに検出し、匿名化します。GDPRに対応したPII匿名化を、デスクトップアプリケーションまたはREST APIとして提供します。",
    "keywords": "Kyveil, PII匿名化, 個人データの匿名化, プライバシー, データ保護, GDPR, Microsoft Presidio, spaCy, flair, ローカルデータ処理",
    "ogTitle": "Kyveil：AIとNLPによるプロフェッショナルなPII匿名化",
    "ogDescription": "個人データを自動で検出し、匿名化します。デスクトップアプリケーションまたはREST APIとして、お客様自身のインフラ上で動作します。",
    "twitterTitle": "Kyveil PII匿名化",
    "twitterDescription": "機密文書のためのローカルなPII検出と匿名化。",
    "url": "https://paioneer-labs.eu/kyveil",
    "structuredData": {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "Kyveil",
      "applicationCategory": "SecurityApplication",
      "operatingSystem": "Windows, macOS, Linux, Server",
      "description": "AIとNLPにより、個人を識別できる情報をローカルで検出し匿名化するソフトウェア。",
      "provider": {
        "@type": "Organization",
        "name": "pAIoneer Labs GmbH",
        "url": "https://paioneer-labs.eu"
      },
      "offers": {
        "@type": "Offer",
        "availability": "https://schema.org/InStock",
        "url": "https://paioneer-labs.eu/kyveil"
      }
    }
  },
  "hero": {
    "eyebrow": "データ保護と匿名化",
    "title": "Kyveil",
    "subtitle": "AI、テスト、共有の前に、ローカルで匿名化。",
    "description": "Kyveilは、ドキュメントおよびAIワークフローのためのローカル匿名化レイヤーです。Word、Excel、PDF、テキストファイル内の個人データを検出し、置換またはマスクしたうえで、AIチャット、テスト、分析、外部共有へ渡す前にプレビューを表示します。Kyveilは [IDD（Intent-Driven Development）](idd) 主導かつ [Veridion](veridion) による自動化のもと、ループ内の人間と共に私たちのAI社員によって開発・継続進化されています。",
    "platforms": "デスクトップアプリケーション、REST API、AnonChat",
    "proofPoints": [
      "文書はお客様のシステム上にローカルで留まります。",
      "個人データは置換またはマスキングされます。",
      "共有やAIチャットの前に、匿名化後の内容をプレビューで確認できます。",
      "Word、Excel、PDF、テキストが中心となる文書ワークフローです。"
    ]
  },
  "dataSovereignty": {
    "title": "お客様のデータ。お客様のシステム。お客様の管理下に。",
    "subtitle": "ローカル処理による完全なデータ主権。",
    "items": [
      {
        "title": "クラウド不要",
        "description": "すべての処理は、お客様のインフラ上でローカルに行われます。ファイルがお客様のシステムの外に出ることはありません。クラウドへの依存も、外部へのデータ送信もありません。"
      },
      {
        "title": "エッジコンピューティング",
        "description": "デスクトップアプリケーションは、ワークステーション上で完全に単独で動作します。APIはお客様自身のサーバーで稼働します。どちらの形態でも、すべてのデータはお客様のセキュリティ境界の内側でローカルに処理されます。"
      },
      {
        "title": "データの自律性",
        "description": "データの流れを完全に管理できます。ベンダーロックインはありません。継続的なクラウド費用も発生しません。機密情報は、常にお客様だけの管理下に置かれます。"
      },
      {
        "title": "標準的なハードウェア",
        "description": "GPUは不要です。一般的なオフィス用PCと標準的なサーバーで動作します。専用ハードウェアへの投資は必要ありません。"
      }
    ]
  },
  "features": {
    "title": "主な機能",
    "subtitle": "業務プロセスにおいて、PIIの検出と匿名化を確実に管理するために必要なすべてを備えています。",
    "items": [
      {
        "title": "AIによるPII検出",
        "description": "Microsoft Presidio SDK、Explosion AIのspaCy、ベルリン・フンボルト大学のflairを用いて自動的に検出します。",
        "tone": "primary"
      },
      {
        "title": "置換モード（Replace Mode）",
        "description": "コンプライアンス用途向けに、検出したPIIを[NAME]、[EMAIL]、[ADDRESS]などの汎用プレースホルダーに置き換えます。",
        "tone": "info"
      },
      {
        "title": "擬似データモード（Fake Mode）",
        "description": "テスト環境や開発向けに、検出したPIIをもっともらしい代替値に置き換えます。",
        "tone": "warning"
      },
      {
        "title": "複数の文書形式に対応",
        "description": "Microsoft Word、Excel、OpenDocument、PDFファイル、プレーンテキストを処理します。",
        "tone": "success"
      },
      {
        "title": "多言語対応",
        "description": "ドイツ語、英語、フランス語、イタリア語、スペイン語に標準で対応し、言語ごとのNLPモデルを使用します。",
        "tone": "secondary"
      },
      {
        "title": "検出対象の設定",
        "description": "氏名、住所、メールアドレス、電話番号、銀行口座、納税者番号など、検出するPIIエンティティを定義できます。",
        "tone": "primary"
      },
      {
        "title": "拡張性",
        "description": "独自の前処理・後処理ロジックを組み込むためのインターフェースを備え、お客様固有のワークフローに統合できます。",
        "tone": "info"
      },
      {
        "title": "CPUに最適化",
        "description": "効率的なモデルアーキテクチャにより、GPUを必要とせず、標準的なオフィス用ハードウェアで動作します。",
        "tone": "warning"
      }
    ]
  },
  "technology": {
    "title": "技術スタック",
    "subtitle": "エンタープライズ向けフレームワークと、実績のあるNLP研究。",
    "items": [
      {
        "title": "Microsoft Presidio SDK",
        "description": "Microsoft Data Protection and De-identification SDK。エンティティ認識機能を標準搭載した、PIIの検出と匿名化のための業界標準フレームワークです。",
        "tone": "primary"
      },
      {
        "title": "spaCy",
        "subtitle": "Explosion AIが開発",
        "description": "Industrial-Strength Natural Language Processing（産業利用に耐える自然言語処理）。固有表現抽出のための事前学習済み統計モデルを備えた、本番環境で使えるNLPライブラリです。",
        "tone": "info"
      },
      {
        "title": "flair",
        "subtitle": "ベルリン・フンボルト大学が開発",
        "description": "文脈を考慮した文字列埋め込みを備えた最先端のNLPフレームワーク。高度なニューラルネットワークモデルにより、エンティティ検出の精度を高めます。",
        "tone": "secondary"
      }
    ],
    "integration": "統合されたAIパイプライン",
    "integrationDesc": "Presidioをオーケストレーション層とし、spaCyとflairのモデルを組み合わせて検出を行うことで、複数の言語とエンティティ種別にわたって最適な検出結果を実現します。"
  },
  "technicalDetails": {
    "title": "技術的な機能",
    "subtitle": "欧州のビジネス文書に対応した、設定可能な検出機能。",
    "items": [
      {
        "title": "対応言語",
        "description": "ドイツ語、英語、フランス語、イタリア語、スペイン語向けの事前学習済みNLPモデル。言語ごとのエンティティ認識により、欧州のビジネス環境でPIIを高精度に検出します。",
        "tone": "primary"
      },
      {
        "title": "設定可能なPIIエンティティ",
        "description": "検出・匿名化する個人識別情報の種類を選択できます。氏名、メールアドレス、電話番号、住所、IBAN（銀行口座番号）、クレジットカード番号、社会保障番号、納税者番号、パスポート番号などに対応しています。",
        "tone": "info"
      },
      {
        "title": "匿名化モード",
        "description": "置換モード（Replace Mode）は、コンプライアンス文書向けにPIIを汎用プレースホルダーに置き換えます。擬似データモード（Fake Mode）は、文書の構造を保ったまま、テスト環境向けにもっともらしい代替値を使用します。",
        "tone": "warning"
      },
      {
        "title": "前処理と後処理",
        "description": "匿名化の前後に独自のロジックを実装できます。提供されるインターフェースを通じて、文書の検証、形式変換、品質チェック、監査ログの記録、後続システムとの連携を追加できます。",
        "tone": "secondary"
      }
    ]
  },
  "anonChat": {
    "title": "AnonChatは、KyveilにAI Webチャットを加えます",
    "subtitle": "この新しいアプリは、外部または社内のAIシステムと実際にチャットを行う前に、同じ匿名化の仕組みを適用します。",
    "eyebrow": "新しいアプリケーション",
    "name": "AnonChat",
    "description": "AnonChatは、WordやExcelの文書をChatGPT、Claude、Gemini、社内LLMで利用する前に匿名化できるAI Webチャットです。内容がチャットに渡される前に、匿名化後のテキストをプレビューで確認できます。",
    "statement": "判断するのはユーザーです。確認し、承認し、それからチャットします。",
    "steps": [
      {
        "title": "文書を読み込む",
        "description": "WordまたはExcelのファイルを、AIワークフローに渡す前に匿名化できるよう準備します。",
        "tone": "primary"
      },
      {
        "title": "PIIを匿名化する",
        "description": "Kyveilが個人データを検出し、選択されたルールに従って置換またはマスキングします。",
        "tone": "info"
      },
      {
        "title": "プレビューを確認する",
        "description": "内容がチャットに渡される前に、匿名化後のテキストを確認できます。",
        "tone": "warning"
      },
      {
        "title": "AIチャットを利用する",
        "description": "確認を終えてはじめて、匿名化済みの内容が外部または社内のLLMとのチャットに使用されます。",
        "tone": "success"
      }
    ]
  },
  "deployment": {
    "title": "導入形態",
    "subtitle": "ユースケースに合ったソリューションをお選びください。",
    "options": [
      {
        "title": "デスクトップアプリケーション",
        "subtitle": "個々のワークステーション向けのスタンドアロンアプリケーション",
        "description": "デスクトップ版は、Windows、macOS、Linux上で完全に独立して動作します。ネットワークに依存せず、自席で直接文書を匿名化する必要があるユーザーに最適です。",
        "features": [
          "100%オフライン対応（インターネット接続不要）",
          "ファイルがローカルのワークステーションの外に出ない設計",
          "複数ファイルの一括処理",
          "操作しやすいグラフィカルユーザーインターフェース",
          "GPUのない標準的なオフィス用PCで動作",
          "買い切りライセンス（サブスクリプションなし）"
        ]
      },
      {
        "title": "REST API",
        "subtitle": "統合とスケーリングのためのサーバー導入",
        "description": "API版は、お客様自身のサーバーまたはプライベートクラウド基盤で稼働します。処理量の多い既存のシステムやワークフローに匿名化を組み込むのに最適です。",
        "features": [
          "自社インフラへの導入（オンプレミスまたはプライベートクラウド）",
          "高スループット要件に対応する水平スケーリング",
          "充実したドキュメントを備えたRESTful API",
          "デスクトップ版と同一の匿名化エンジン",
          "主要なプラットフォーム向けの統合サンプル",
          "前処理・後処理のカスタマイズに対応"
        ]
      },
      {
        "title": "AnonChat",
        "subtitle": "チャットの前に匿名化を行うAI Webチャット",
        "description": "AnonChatは、実際のAIチャットの手前にKyveilを配置します。WordやExcelの文書を匿名化し、プレビューを確認したうえで、はじめて内容をChatGPT、Claude、Gemini、社内LLMで利用します。",
        "features": [
          "事後のチェックではなく、チャットの前に匿名化",
          "受け渡しの前に匿名化後のテキストをプレビュー",
          "文書に基づくAIシステムへの質問に最適",
          "個人データを外部モデルへ送信するリスクを低減",
          "社内LLMやエンタープライズ向けチャット環境に接続可能",
          "デスクトップ版およびREST APIと同じ匿名化の仕組みを採用"
        ]
      }
    ],
    "bothNote": "デスクトップアプリケーション、REST API、AnonChatはいずれも、機密性の高い内容を後続の処理に渡す前に匿名化できるよう設計されています。"
  },
  "useCases": {
    "title": "ユースケース",
    "subtitle": "GDPRへの準拠から、安全な開発用データまで。",
    "items": [
      {
        "title": "GDPRへの準拠",
        "description": "文書を社外と共有する前や社内の他部門で利用する前に、個人データを匿名化します。テストデータ、分析用データセット、ドキュメントに関するデータ保護要件を満たせます。",
        "tone": "primary"
      },
      {
        "title": "テストデータの準備",
        "description": "現実に即したテストデータセットを用意し、開発者が実在の個人情報をさらすことなく、本番に近いデータで作業できるようにします。",
        "tone": "info"
      },
      {
        "title": "人事文書の処理",
        "description": "応募書類、従業員ファイル、人事関連文書を匿名化し、プライバシーを守りながら、先入観のない選考、コンプライアンス監査、データ分析を可能にします。",
        "tone": "warning"
      },
      {
        "title": "データ分析",
        "description": "機密情報をあらかじめ匿名化することで、PIIを含む文書の分析を可能にします。データ保護上の懸念なく、データセットをチーム間で共有できます。",
        "tone": "success"
      },
      {
        "title": "文書のアーカイブ",
        "description": "保存ポリシーとデータ保護法令に従ってPIIを削除または匿名化し、文書を長期保存に適した状態にします。",
        "tone": "secondary"
      },
      {
        "title": "安全な共有",
        "description": "機密性の高い個人情報を自動的に削除したうえで、外部パートナー、委託先、他部門と文書を共有できます。",
        "tone": "error"
      }
    ]
  },
  "releases": {
    "title": "提供形態とサポート",
    "subtitle": "本番利用のためのアップデート、機能拡張、サポート。",
    "items": [
      {
        "title": "デスクトップ、API、AnonChat",
        "description": "Kyveilは、デスクトップアプリケーションと、統合用のREST APIとして提供しています。AnonChatは、チャットの前に文書を匿名化するWebチャットです。",
        "tone": "info"
      },
      {
        "title": "サポート",
        "description": "企業でのご利用に向けて、技術サポート、導入支援、カスタマイズを提供しています。",
        "tone": "success"
      }
    ]
  },
  "cta": {
    "title": "Kyveilの導入を始めましょう",
    "subtitle": "機密文書を、AI、テスト、分析、共有に渡る前に保護します。",
    "desktop": "デスクトップ版を申し込む",
    "desktopSubject": "Kyveil Desktop",
    "apiContact": "REST APIの導入についてお問い合わせ",
    "contact": "デモやお見積もりのご依頼は、メールでご連絡ください。",
    "email": "info@paioneer.eu",
    "apiSubject": "Kyveil REST APIの導入について",
    "contactSubject": "Kyveilのデモまたはお見積もりについて"
  }
};
